import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { dash } from '@/components/ui/data-table';
import {
  GoneState,
  StaleNotice,
  UnresolvedState,
  isNotFound,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { WithOrgClock } from '../ui/WithOrgClock';
import { todayISO } from '../../lib/format';
import { ASSET_STATUS, display } from '../status';
import { IconFleet } from '../icons';
import { ASSET_KIND_LABEL } from './assetVocab';
import { EditAssetSheet } from './AssetSheet';
import { AssetMaintenanceCard, type MaintenanceRow } from './AssetMaintenanceCard';
import { AssignmentEditor, type Assignment } from './AssignmentEditor';
import { PlacementEditor } from './PlacementEditor';

/**
 * One van, or one tool (107) — body config A: the record's working parts down the middle,
 * the facts about it in the rail.
 *
 * Gated behind `WithOrgClock` because two controls on this page write a DATE somebody
 * reads back later: the day an assignment starts, and the day a service is due. A default
 * taken from the reader's laptop dates a New York shop's evening entry tomorrow — the same
 * defect `?? 'America/New_York'` used to cause on nine other screens, pointed at a stored
 * value instead of a displayed one.
 */
export function AssetDetailPage() {
  return (
    <WithOrgClock what="this asset" icon={IconFleet} title="Asset">
      {(tz) => <AssetDetail today={todayISO(tz)} tz={tz} />}
    </WithOrgClock>
  );
}

function AssetDetail({ today, tz }: { today: string; tz: string }) {
  const { assetId } = useParams({ from: '/office/fleet/assets/$assetId' });
  const trpc = useTRPC();
  const q = useQuery(trpc.asset.get.queryOptions({ assetId }));
  const [editing, setEditing] = useState(false);

  const state = shownState(q, q.data);
  if (!q.data) {
    return (
      <PageRoot>
        <BackCrumb to="/fleet/assets" label="Fleet" />
        {/*
         * Two ways this record isn't here, and only one of them is worth a retry. The
         * service returns `null` — not NOT_FOUND — for a record that is gone AND for one
         * the caller may not see; those are indistinguishable on purpose (the visit
         * idiom). `state === 'ready'` with no data is exactly that answered-and-empty
         * case, so it reads as gone rather than as a failure with a button that can only
         * fail. `isNotFound` covers the shape where the server does say so.
         */}
        {state === 'ready' || isNotFound(q.error) ? (
          <GoneState
            icon={IconFleet}
            what="asset"
            backTo="/fleet/assets"
            backLabel="Back to the fleet"
          />
        ) : (
          <UnresolvedState
            state={unresolved(state)}
            what="this asset"
            icon={IconFleet}
            error={q.error}
            onRetry={() => void q.refetch()}
            skeleton="h-64"
          />
        )}
      </PageRoot>
    );
  }

  const a = q.data;
  const s = display(ASSET_STATUS, a.status);
  const isVehicle = a.kind === 'vehicle';
  const maintenance = a.maintenance as MaintenanceRow[];
  const assignments = a.assignments as Assignment[];
  // The service settled this on the SHOP's clock and shipped it as a field. Re-deriving
  // it here from `startsOn`/`endsOn` is what had this page naming a tech the register had
  // already dropped, for the five hours a day the two clocks disagreed.
  const holder = assignments.find((x) => x.phase === 'current');

  return (
    <PageRoot>
      <BackCrumb to="/fleet/assets" label="Fleet" />
      <DetailHeader
        title={a.name}
        badge={<StatusBadge tone={s.tone}>{s.label}</StatusBadge>}
        right={
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Edit
          </Button>
        }
      />
      <MetaStrip
        items={[
          { label: 'Kind', value: ASSET_KIND_LABEL[a.kind] ?? a.kind },
          ...(isVehicle
            ? [
                { label: 'Plate', value: dash(a.plate) },
                {
                  label: 'Make/model',
                  value: dash([a.make, a.model].filter(Boolean).join(' ')),
                },
              ]
            : [
                { label: 'Serial', value: dash(a.serialNo) },
                { label: 'Category', value: dash(a.category) },
              ]),
          { label: 'Assigned to', value: dash(holder?.userName) },
        ]}
      />

      {/*
       * ⭐ Every control on this page writes against what is drawn here — mark a service
       * done, end an assignment — and each one invalidates. If that refetch fails, the
       * card goes on showing the entry as owed and the tech as current, contradicting
       * the thing the shop just watched succeed. Stale is usually merely old; once a
       * write has landed against it, it is wrong.
       */}
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />

      {a.status === 'retired' ? (
        <Notice tone="info">
          This one is retired. Its record stays here — that is the whole reason it was
          written down.
        </Notice>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SectionCard title="Maintenance &amp; reminders">
            <AssetMaintenanceCard
              assetId={a.id}
              rows={maintenance}
              today={today}
              tz={tz}
              disabled={a.status === 'retired'}
            />
          </SectionCard>

          <SectionCard title="Who runs it">
            <AssignmentEditor
              assetId={a.id}
              assetKind={a.kind}
              assetStatus={a.status}
              assignments={assignments}
              today={today}
            />
          </SectionCard>

          {/*
           * ⚠️ A SECOND card, under its own heading — never folded into "Who runs it".
           * A tool has two holders and nothing reconciles them: placement answers "what is
           * on this van", assignment answers "who is responsible", and a shop really does
           * hand a tech a van full of somebody else's tools (114 part 6 / ruling O1-A).
           *
           * Tools only: a van does not ride in a van, and the server refuses to place one.
           */}
          {a.kind === 'tool' ? (
            <SectionCard title="Which van it rides in">
              <PlacementEditor assetId={a.id} assetStatus={a.status} today={today} />
            </SectionCard>
          ) : null}
        </div>

        <div className="space-y-4">
          {isVehicle ? (
            <SectionCard title="Home base">
              <p className="text-sm">{dash(a.homeBaseAddress)}</p>
              {/*
               * ⭐ A vehicle without coordinates prints nothing, never 0,0 — the spec's
               * own words. The sweep runs nightly, so "not placed yet" is an ordinary
               * state on the day a van is added, not a failure worth a red badge.
               */}
              <p className="mt-2 text-xs text-muted-foreground">
                {a.latitude !== null && a.longitude !== null
                  ? 'Placed on the map for route planning.'
                  : a.homeBaseAddress
                    ? "Not placed on the map yet — it's picked up on the next nightly pass."
                    : 'Add a home base and routes can start from where this van actually sleeps.'}
              </p>
              {a.capacityNote ? (
                <p className="mt-3 border-t border-border pt-3 text-sm">
                  {a.capacityNote}
                </p>
              ) : null}
            </SectionCard>
          ) : null}

          {isVehicle ? (
            <SectionCard title="Parts on board">
              {a.stockLocation ? (
                <>
                  <Link to="/inventory" className="text-sm underline decoration-dotted">
                    {a.stockLocation.name}
                  </Link>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Stock carried on this van is counted in Inventory under that location.
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Not linked to a parts location. Link one from Edit and this van&apos;s
                  stock lines up with the register.
                </p>
              )}
            </SectionCard>
          ) : (
            <SectionCard title="Where it lives">
              <p className="text-sm text-muted-foreground">
                Tools travel with whoever is assigned them — see &ldquo;Who runs
                it&rdquo;.
              </p>
            </SectionCard>
          )}
        </div>
      </div>

      {/*
       * ⭐ Conditional AND keyed, which is how every other edit sheet in this app is
       * mounted and for the reason CustomerDetailPage states: a sheet that keeps stale
       * state is how you edit the wrong thing without noticing. `EditAssetSheet` seeds
       * its draft with `useState(original)` — once, ever — so an always-mounted sheet
       * kept the draft from the LAST time it was opened. Retire the van, reopen Edit, and
       * the Status select read "In service" under a "Retired" badge with Save enabled:
       * one click put the van the shop had just retired back on the register. It also
       * made the discard confirm a lie — "whatever you typed here will be lost" handed
       * the typing straight back.
       */}
      {editing ? (
        <EditAssetSheet
          key={a.id}
          open
          onClose={() => setEditing(false)}
          asset={a}
          onArchived={() => {
            setEditing(false);
            // Retiring does not remove the record and this page still resolves — staying
            // put is what shows the shop what actually happened to it.
          }}
        />
      ) : null}
    </PageRoot>
  );
}
